import { create } from "zustand";
import type { BrokerScore, GameEvent, NationAgentView, NationId, SessionResult } from "@cwb/core";
import type {
  DynamicState,
  MapPayload,
  RoundClock,
  SessionSpeed,
  WorldPatch,
  WorldSnapshot,
} from "@cwb/protocol";
import { recordWealth } from "../ui/wealthHistory";
import type { WealthSample } from "../ui/wealthHistory";
import { readBrowserCompanyName } from "../companyIdentity";

/**
 * The single client-side store.
 *
 * React renders from it and Phaser subscribes to it — neither owns game state,
 * they both mirror what the server said. Nothing in here ever mutates money,
 * inventory or territory; those only ever arrive from a server snapshot (§18).
 *
 * `wealth` is the one thing here the server does not send: the round's net worth
 * is a running series and every message carries only its latest value, so the
 * history is kept where the messages land. It is derived, never authored — each
 * entry is a figure the server sent, filed under the tick it was sent for.
 */

const MAX_EVENTS = 80;

/** The one reading a patch or a snapshot contributes to the wealth series. */
const sampleOf = (message: WorldPatch | WorldSnapshot): WealthSample => ({
  tick: message.state.tick,
  netWorth: message.score.netWorth,
  badDebt: message.score.badDebt,
});

export interface Banner {
  key: number;
  title: string;
  subtitle: string;
  tone: "war" | "trade" | "ai";
}

export interface Toast {
  key: number;
  text: string;
  tone: "info" | "error" | "success";
}

export interface GameStoreState {
  // ---- connection ----
  sessionId: string | null;
  connected: boolean;
  aiMode: string;
  /**
   * The server's own reason for not running the mode that was asked for.
   *
   * Kept because the client cannot work it out: no credential and every live
   * slot already taken produce the same screen — a pill that says 모의 에이전트
   * and no switch beside it — and only the server knows which happened.
   */
  aiModeNote: string | null;
  runId: string | null;
  seed: string | null;
  bootError: string | null;
  /** First identity, persistent when available and otherwise fixed in tab memory. */
  companyName: string | null;

  // ---- world ----
  map: MapPayload | null;
  state: DynamicState | null;
  agents: NationAgentView[];
  score: BrokerScore | null;
  /** Net worth sampled across the round, for the chart on the broker ledger. */
  wealth: WealthSample[];
  events: GameEvent[];
  result: SessionResult | null;
  /** Last authoritative clock rate received from the server. */
  simulationSpeed: SessionSpeed;
  /** Ranked duration and remaining time, both in authoritative simulation ticks. */
  roundClock: RoundClock | null;

  // ---- ui ----
  selectedNationId: NationId | null;
  selectedProvinceId: string | null;
  /**
   * What the pointer is over on the map.
   *
   * Published by the scene because the canvas is the only thing that knows
   * which polygon a pixel belongs to. It used to carry the cursor position too,
   * for a dossier that hung off the pointer; the dossier is docked now, so the
   * id is the whole of what React needs.
   */
  hoveredProvinceId: string | null;
  banner: Banner | null;
  toast: Toast | null;
  tutorialDismissed: boolean;
  pendingRequest: boolean;

  // ---- actions ----
  applySnapshot: (snapshot: WorldSnapshot) => void;
  applyPatch: (patch: WorldPatch) => void;
  pushEvents: (events: GameEvent[]) => void;
  upsertAgent: (agent: NationAgentView) => void;
  setConnected: (connected: boolean) => void;
  setBootError: (error: string | null) => void;
  setAiModeNote: (note: string | null) => void;
  setCompanyName: (companyName: string) => void;
  setResult: (result: SessionResult) => void;
  setSimulationSpeed: (speed: SessionSpeed) => void;
  selectNation: (nationId: NationId | null) => void;
  selectProvince: (provinceId: string | null) => void;
  setHoveredProvince: (provinceId: string | null) => void;
  showBanner: (title: string, subtitle: string, tone: Banner["tone"]) => void;
  clearBanner: () => void;
  showToast: (text: string, tone: Toast["tone"]) => void;
  clearToast: () => void;
  dismissTutorial: () => void;
  setPendingRequest: (pending: boolean) => void;
  reset: () => void;
}

let bannerKey = 0;
let toastKey = 0;

export const useGameStore = create<GameStoreState>((set, get) => ({
  sessionId: null,
  connected: false,
  aiMode: "mock",
  aiModeNote: null,
  runId: null,
  seed: null,
  bootError: null,
  companyName: readBrowserCompanyName(),

  map: null,
  state: null,
  agents: [],
  score: null,
  wealth: [],
  events: [],
  result: null,
  simulationSpeed: 0,
  roundClock: null,

  selectedNationId: null,
  selectedProvinceId: null,
  hoveredProvinceId: null,
  banner: null,
  toast: null,
  tutorialDismissed: false,
  pendingRequest: false,

  applySnapshot: (snapshot) => {
    // The geography is static for a whole run — that is why it is sent apart
    // from the state. The broker fleet moves, but its visible position derives
    // from the tick rather than replacing the map object. A full snapshot goes out every five seconds and
    // carries it again, so writing it in unconditionally handed the scene a new
    // object on every snapshot; the scene compares by identity, decided the map
    // had changed, rebuilt its static geometry and refitted the camera. The
    // symptom was the camera resetting every five seconds while the player was
    // panning. Same run, same object.
    const previous = get();
    const sameRun = previous.map !== null && previous.runId === snapshot.runId;

    set({
      sessionId: snapshot.sessionId,
      runId: snapshot.runId,
      seed: snapshot.seed,
      aiMode: snapshot.aiMode,
      simulationSpeed: snapshot.simulationSpeed,
      roundClock: { ...snapshot.roundClock },
      map: sameRun ? previous.map : snapshot.map,
      state: snapshot.state,
      agents: snapshot.agents,
      score: snapshot.score,
      wealth: sameRun ? recordWealth(previous.wealth, sampleOf(snapshot)) : [sampleOf(snapshot)],
      bootError: null,
    });
  },

  applyPatch: (patch) =>
    set((prev) => ({
      sessionId: patch.sessionId,
      simulationSpeed: patch.simulationSpeed,
      roundClock: { ...patch.roundClock },
      state: patch.state,
      agents: patch.agents,
      score: patch.score,
      wealth: recordWealth(prev.wealth, sampleOf(patch)),
    })),

  pushEvents: (incoming) => {
    if (incoming.length === 0) return;
    const existing = get().events;
    const seen = new Set(existing.map((e) => e.id));
    const fresh = incoming.filter((e) => !seen.has(e.id));
    if (fresh.length === 0) return;
    set({ events: [...fresh.reverse(), ...existing].slice(0, MAX_EVENTS) });
  },

  upsertAgent: (agent) =>
    set((prev) => {
      const next = prev.agents.filter((a) => a.nationId !== agent.nationId);
      next.push(agent);
      next.sort((a, b) => a.nationId.localeCompare(b.nationId));
      return { agents: next };
    }),

  setConnected: (connected) => set({ connected }),
  setBootError: (bootError) => set({ bootError }),
  setAiModeNote: (aiModeNote) => set({ aiModeNote }),
  setCompanyName: (companyName) => {
    const existing = get().companyName;
    if (existing === null || existing === companyName) set({ companyName });
  },
  setResult: (result) => set({ result }),
  setSimulationSpeed: (simulationSpeed) => set({ simulationSpeed }),

  selectNation: (selectedNationId) => set({ selectedNationId }),

  // Called on every pointer move, so it writes only when the province actually
  // changed. Setting state on each mousemove would re-render every subscriber
  // sixty times a second. Dropping the cursor position made this exact: the
  // dossier is docked, so nothing on screen moves while the pointer stays on
  // the same ground, and a move within one province is now genuinely a no-op.
  setHoveredProvince: (provinceId) => {
    if (get().hoveredProvinceId === provinceId) return;
    set({ hoveredProvinceId: provinceId });
  },
  selectProvince: (selectedProvinceId) => {
    const map = get().map;
    const state = get().state;
    if (selectedProvinceId && map && state) {
      const owner = state.provinces.find((p) => p.id === selectedProvinceId)?.ownerNationId ?? null;
      set({ selectedProvinceId, selectedNationId: owner });
      return;
    }
    set({ selectedProvinceId });
  },

  showBanner: (title, subtitle, tone) => {
    bannerKey += 1;
    set({ banner: { key: bannerKey, title, subtitle, tone } });
  },
  clearBanner: () => set({ banner: null }),

  showToast: (text, tone) => {
    toastKey += 1;
    set({ toast: { key: toastKey, text, tone } });
  },
  clearToast: () => set({ toast: null }),

  dismissTutorial: () => set({ tutorialDismissed: true }),
  setPendingRequest: (pendingRequest) => set({ pendingRequest }),

  reset: () =>
    set({
      sessionId: null,
      connected: false,
      map: null,
      state: null,
      agents: [],
      score: null,
      wealth: [],
      events: [],
      result: null,
      simulationSpeed: 0,
      roundClock: null,
      selectedNationId: null,
      selectedProvinceId: null,
      hoveredProvinceId: null,
      banner: null,
      toast: null,
      pendingRequest: false,
    }),
}));

// ---------------------------------------------------------------------------
// selectors — keep component subscriptions narrow
// ---------------------------------------------------------------------------

export const selectNationById = (nationId: NationId | null) => (s: GameStoreState) =>
  nationId ? (s.state?.nations.find((n) => n.id === nationId) ?? null) : null;

export const selectMapNation = (nationId: NationId | null) => (s: GameStoreState) =>
  nationId ? (s.map?.nations.find((n) => n.id === nationId) ?? null) : null;

export const selectAgent = (nationId: NationId | null) => (s: GameStoreState) =>
  nationId ? (s.agents.find((a) => a.nationId === nationId) ?? null) : null;

/** #RRGGBB for CSS, from the packed 0xRRGGBB the core stores. */
export function nationColorCss(color: number | undefined): string {
  if (color === undefined) return "#94a3b8";
  return `#${color.toString(16).padStart(6, "0")}`;
}
